import { useState } from 'react';
import { FolderOpen, Calendar, ChevronRight, Plus, Edit, Trash2 } from 'lucide-react';
import type { Project } from '../../../types/project';
import type { Category, Task } from '../../../App';
import type { PINCode } from '../VendorsScreen';
import type { MicrosoftAccount, SupervisorAccess } from '../PINCodesScreen';
import { ProjectFormModal } from './ProjectFormModal';

interface ProjectsListScreenProps {
  onSelectProject: (projectId: string) => void;
  categories: Category[];
  allTasks: Task[];
  projects: Project[];
  pinCodes: PINCode[];
  supervisorAccess: SupervisorAccess | null;
  microsoftAccounts: MicrosoftAccount[];
  onAddProject: (project: Project) => void;
  onUpdateProject: (project: Project) => void;
  onDeleteProject: (projectId: string) => void;
  onNavigateToManageAccess?: () => void;
}

export function ProjectsListScreen({ onSelectProject, categories, allTasks, projects, pinCodes, supervisorAccess, microsoftAccounts, onAddProject, onUpdateProject, onDeleteProject, onNavigateToManageAccess }: ProjectsListScreenProps) {
  const [selectedStatus, setSelectedStatus] = useState<'all' | 'active' | 'completed' | 'on-hold'>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);

  const filteredProjects = selectedStatus === 'all' 
    ? projects 
    : projects.filter(p => p.status === selectedStatus);

  const getStatusColor = (status: Project['status']) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-700';
      case 'completed':
        return 'bg-blue-100 text-blue-700';
      case 'on-hold':
        return 'bg-yellow-100 text-yellow-700';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleAddProject = () => {
    setEditingProject(null);
    setIsModalOpen(true);
  };

  const handleEditProject = (project: Project) => {
    setEditingProject(project);
    setIsModalOpen(true);
  };

  const handleDeleteProject = (projectId: string) => {
    if (confirm('Are you sure you want to delete this project?')) {
      onDeleteProject(projectId);
    }
  };

  const handleSaveProject = (project: Project) => {
    if (editingProject) {
      onUpdateProject(project);
    } else {
      onAddProject(project);
    }
    setIsModalOpen(false);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Projects</h2>
          <p className="text-gray-600">Select a project to view detailed timeline reports</p>
        </div>
        <button
          onClick={handleAddProject}
          className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium bg-indigo-600 text-white hover:bg-indigo-700 transition"
        >
          <Plus className="w-4 h-4" />
          New Project
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setSelectedStatus('all')}
          className={`px-4 py-2 rounded-lg font-medium transition ${
            selectedStatus === 'all'
              ? 'bg-indigo-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          All Projects
        </button>
        <button
          onClick={() => setSelectedStatus('active')}
          className={`px-4 py-2 rounded-lg font-medium transition ${
            selectedStatus === 'active'
              ? 'bg-indigo-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Active
        </button>
        <button
          onClick={() => setSelectedStatus('completed')}
          className={`px-4 py-2 rounded-lg font-medium transition ${
            selectedStatus === 'completed'
              ? 'bg-indigo-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Completed
        </button>
        <button
          onClick={() => setSelectedStatus('on-hold')}
          className={`px-4 py-2 rounded-lg font-medium transition ${
            selectedStatus === 'on-hold'
              ? 'bg-indigo-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          On Hold
        </button>
      </div>

      {/* Projects List */}
      <div className="space-y-3">
        {filteredProjects.map((project) => (
          <button
            key={project.id}
            onClick={() => onSelectProject(project.id)}
            className="w-full bg-white border border-gray-200 rounded-lg p-4 hover:border-indigo-500 hover:shadow-md transition text-left group"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3 flex-1">
                <div className="p-2 bg-indigo-50 rounded-lg group-hover:bg-indigo-100 transition">
                  <FolderOpen className="w-5 h-5 text-indigo-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="font-semibold text-gray-900">{project.name}</h3>
                    <span className={`text-xs px-2 py-1 rounded ${getStatusColor(project.status)}`}>
                      {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <span className="font-mono">{project.number}</span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      Created {formatDate(project.dateCreated)}
                    </span>
                    <span>{project.tasks.length} tasks</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Edit
                  className="w-5 h-5 text-gray-400 group-hover:text-indigo-600 transition cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditProject(project);
                  }}
                />
                <Trash2
                  className="w-5 h-5 text-gray-400 group-hover:text-red-600 transition cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteProject(project.id);
                  }}
                />
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-indigo-600 transition" />
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Project Form Modal */}
      <ProjectFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        categories={categories}
        allTasks={allTasks}
        project={editingProject}
        pinCodes={pinCodes}
        supervisorAccess={supervisorAccess}
        microsoftAccounts={microsoftAccounts}
        onSave={handleSaveProject}
        onNavigateToManageAccess={onNavigateToManageAccess || (() => {})}
      />
    </div>
  );
}
